import { DescribeRegionsCommand, EC2Client } from '@aws-sdk/client-ec2'
import {
  AccessKey,
  CreateAccessKeyCommand,
  CreateUserCommand,
  GetUserCommand,
  IAMClient,
  PutUserPolicyCommand,
  User,
} from '@aws-sdk/client-iam'
import {
  CreateBucketCommand,
  PutBucketVersioningCommand,
  PutPublicAccessBlockCommand,
  S3Client,
} from '@aws-sdk/client-s3'
import R from 'ramda'
import { Credentials, UtilsConfig } from './types'

const extractRegions = R.pipe(R.propOr([], 'Regions'), R.pluck('RegionName'), R.reject(R.isNil))
export class Utils {
  private credentials: Credentials
  private region: string | undefined
  private defaultRegion = 'us-east-1'

  constructor({ credentials, region }: UtilsConfig) {
    this.credentials = credentials
    this.region = region
  }

  setCredentials(credentials: Credentials): void {
    this.credentials = credentials
  }

  setRegion(region: string): void {
    this.region = region
  }

  getRegions = async (): Promise<string[]> => {
    const ec2Client = new EC2Client({
      credentials: this.credentials,
      region: this.region ?? 'us-east-1',
    })
    const command = new DescribeRegionsCommand({})
    const result = await ec2Client.send(command)
    return extractRegions(result)
  }

  getUser = async (userName: string): Promise<User | undefined> => {
    const iamClient = new IAMClient({
      credentials: this.credentials,
      region: this.region ?? 'us-east-1',
    })
    const command = new GetUserCommand({ UserName: userName })
    const result = await iamClient.send(command)
    return result?.User
  }

  createS3Bucket = async (bucketName: string): Promise<void> => {
    const s3Client = new S3Client({
      credentials: this.credentials,
      region: this.region ?? this.defaultRegion,
    })
    const command = new CreateBucketCommand({
      Bucket: bucketName,
      ACL: 'private',
    })
    await s3Client.send(command)

    const enableVersioningcommand = new PutBucketVersioningCommand({
      Bucket: bucketName,
      VersioningConfiguration: {
        Status: 'Enabled',
      },
    })
    await s3Client.send(enableVersioningcommand)

    const putPublicAccessBlockCommand = new PutPublicAccessBlockCommand({
      Bucket: bucketName,
      PublicAccessBlockConfiguration: {
        BlockPublicAcls: true,
        BlockPublicPolicy: true,
        IgnorePublicAcls: true,
      },
    })
    await s3Client.send(putPublicAccessBlockCommand)
  }

  createUser = async (userName: string): Promise<User | undefined> => {
    const iamClient = new IAMClient({
      credentials: this.credentials,
      region: this.region ?? this.defaultRegion,
    })
    const command = new CreateUserCommand({
      UserName: userName,
      Tags: [{ Key: 'Name', Value: 'Fastlane Match IAM User' }],
    })
    const result = await iamClient.send(command)
    return result?.User
  }

  createAccessKey = async (userName: string): Promise<AccessKey | undefined> => {
    const iamClient = new IAMClient({
      credentials: this.credentials,
      region: this.region ?? this.defaultRegion,
    })

    const command = new CreateAccessKeyCommand({
      UserName: userName,
    })
    const result = await iamClient.send(command)
    return result?.AccessKey
  }

  grantBucketReadAccess = async (user: User, bucketName: string): Promise<void> => {
    const iamClient = new IAMClient({
      credentials: this.credentials,
      region: this.region ?? this.defaultRegion,
    })

    const policy = {
      Version: '2012-10-17',
      Statement: [
        {
          Effect: 'Allow',
          Action: ['s3:ListBucket'],
          Resource: `arn:aws:s3:::${bucketName}`,
        },
        {
          Effect: 'Allow',
          Action: ['s3:GetObject', 's3:GetObjectAcl'],
          Resource: `arn:aws:s3:::${bucketName}/*`,
        },
      ],
    }

    const command = new PutUserPolicyCommand({
      UserName: user.UserName,
      PolicyName: 'MatchS3BucketReadAccess',
      PolicyDocument: JSON.stringify(policy),
    })

    await iamClient.send(command)
  }
}
